import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { CircularProgress, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import BlockIcon from '@material-ui/icons/Block';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SpeedIcon from '@material-ui/icons/Speed';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import StorageIcon from '@material-ui/icons/Storage';
import FlightIcon from '@material-ui/icons/Flight';
import { EmptyCollection, Pagination } from '../../../../shared_elements';
import { STableLoader } from '../../../../shared_elements/loaders';
import { getFileSize, capitalizeFirstLetter } from '../../../../utils';
import { getUploadSessionsApi } from '../apiServices';
import { SESSION_STATUS, sessionStatusConfig } from '../constants';
import { useLiveUploads, LIVE_STATUS } from '../liveUploads';

// Poll while a session is still active; uploaded-bytes deltas between polls give the upload speed
const POLL_INTERVAL_MS = 5000;
const TRACK_COLOR = '#e8ecf2';
const DEFAULT_PER_PAGE = 20;

const useStyles = makeStyles({
  header: { padding: '16px 18px', marginBottom: 12, borderRadius: 8 },
  headerTop: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 18 },
  ring: { position: 'relative', width: 72, height: 72, flexShrink: 0 },
  ringLayer: { position: 'absolute', left: 0, top: 0 },
  ringLabel: { position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#1f2937' },
  headline: { flex: '1 1 260px', minWidth: 0 },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: '#1f2937', '& svg': { fontSize: 20 } },
  subtitle: { fontSize: 13, color: '#4b5563', marginTop: 3, '& b': { color: '#1f2937' } },
  metrics: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  metric: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, background: '#f6f8fb', border: '1px solid #edf0f5', minWidth: 112,
    '& svg': { fontSize: 18, color: '#6b7280' },
    '& span': { display: 'block', fontSize: 11, color: '#6b7280', lineHeight: 1.3 },
    '& strong': { display: 'block', fontSize: 13, fontWeight: 600, color: '#1f2937', lineHeight: 1.3, whiteSpace: 'nowrap' },
  },
  listHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 12px 6px 16px', borderBottom: '1px solid #edf0f5' },
  listTitle: { fontSize: 14, fontWeight: 600, color: '#1f2937', '& span': { marginLeft: 6, fontSize: 12, fontWeight: 500, color: '#6b7280' } },
  refreshInfo: { display: 'flex', alignItems: 'center', fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' },
  row: { '& td': { paddingTop: 8, paddingBottom: 8 } },
  assetCell: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  assetIcon: { width: 30, height: 30, borderRadius: 8, background: '#eef3ff', color: '#377ff0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': { fontSize: 17 } },
  assetName: { fontSize: 13, fontWeight: 500, color: '#1f2937', whiteSpace: 'nowrap' },
  subText: { fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' },
  filesCount: { fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap', '& b': { fontWeight: 600 } },
  progressCell: { width: 220 },
  progressRow: { display: 'flex', alignItems: 'center', gap: 8, '& > span': { fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right', color: '#1f2937' } },
  progressBar: { flex: 1, height: 6, borderRadius: 4, backgroundColor: TRACK_COLOR },
  // Bar colour comes from the --bar-color custom property set per status on the root element
  progressBarFill: { borderRadius: 4, backgroundColor: 'var(--bar-color, #377ff0)' },
  progressNote: { fontSize: 11, color: '#6b7280', marginTop: 3, whiteSpace: 'nowrap' },
  statusChip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px 2px 6px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', '& svg': { fontSize: 15 } },
  muted: { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' },
});

const statusIcons = {
  [SESSION_STATUS.ACTIVE]: <CloudUploadIcon />,
  [SESSION_STATUS.COMPLETED]: <CheckCircleIcon />,
  [SESSION_STATUS.FAILED]: <ErrorIcon />,
  [SESSION_STATUS.CANCELLED]: <BlockIcon />,
};

// Unknown statuses from the API still render, labelled with the raw value
const getConfig = (status) => sessionStatusConfig[status] || { label: capitalizeFirstLetter((status || 'Unknown').toLowerCase()), color: '#64748b', background: '#edf1f7', bar: '#94a3b8' };

const formatDuration = (seconds) => {
  if (!seconds || !isFinite(seconds)) return null;
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const RelativeTime = ({ value, classes }) => value ?
  <Tooltip arrow title={moment(value).format('MMM DD, YYYY hh:mm A')}>
    <span className={classes.muted}>{moment(value).fromNow()}</span>
  </Tooltip> : <span className={classes.muted}>--</span>;

const StatusChip = ({ status, classes }) => {
  const config = getConfig(status);
  return (
    <span className={classes.statusChip} style={{ color: config.color, background: config.background }}>
      {statusIcons[status] || null} {config.label}
    </span>
  );
};

const SessionProgress = ({ session, classes }) => {
  const config = getConfig(session.status);
  const percentage = Math.min(100, session.progress_percentage || 0);
  return (
    <div className={classes.progressCell}>
      <div className={classes.progressRow}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          className={classes.progressBar}
          classes={{ bar: classes.progressBarFill }}
          style={{ '--bar-color': config.bar }}
        />
        <span>{Math.floor(percentage)}%</span>
      </div>
      <div className={classes.progressNote}>
        {getFileSize(session.uploaded_bytes)} of {getFileSize(session.total_bytes)}
        {session.isLive && session.liveSpeed > 0 ? ` · ${getFileSize(session.liveSpeed)}/s` : ''}
      </div>
    </div>
  );
};

const ProgressRing = ({ value, done, classes }) => (
  <div className={classes.ring}>
    <CircularProgress variant="determinate" value={100} size={72} thickness={4.5} className={classes.ringLayer} style={{ color: TRACK_COLOR }} />
    <CircularProgress variant="determinate" value={value} size={72} thickness={4.5} className={classes.ringLayer} style={{ color: done ? sessionStatusConfig.COMPLETED.bar : sessionStatusConfig.ACTIVE.bar }} />
    <div className={classes.ringLabel}>{Math.floor(value)}%</div>
  </div>
);

const Metric = ({ icon, label, value, classes }) => (
  <div className={classes.metric}>
    {icon}
    <div><span>{label}</span><strong>{value}</strong></div>
  </div>
);

const sumBy = (list, key) => list.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

// The server only learns a file finished later, so sessions uploading from this tab show the
// browser's own numbers (never lower than what the server already reports)
const withLiveProgress = (session, live) => {
  if (!live) return session;
  const totalBytes = session.total_bytes || live.totalBytes || 0;
  const uploadedBytes = Math.max(session.uploaded_bytes || 0, live.uploadedBytes || 0);
  const completedFiles = Math.max(session.completed_files || 0, live.completedFiles || 0);
  let status = SESSION_STATUS.ACTIVE;
  if (live.status === LIVE_STATUS.DONE) {
    status = live.failedFiles && !completedFiles ? SESSION_STATUS.FAILED : SESSION_STATUS.COMPLETED;
  }
  return {
    ...session,
    completed_files: completedFiles,
    failed_files: Math.max(session.failed_files || 0, live.failedFiles || 0),
    uploaded_bytes: uploadedBytes,
    progress_percentage: totalBytes ? (uploadedBytes / totalBytes) * 100 : session.progress_percentage,
    status,
    liveSpeed: live.status === LIVE_STATUS.UPLOADING ? live.speed : 0,
    isLive: live.status === LIVE_STATUS.UPLOADING,
  };
};

const UploadProgress = () => {
  const classes = useStyles();
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [sessions, setSessions] = useState({ list: [], pagination: null });
  const [speed, setSpeed] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const lastQuery = useRef({ page: 1, per_page: DEFAULT_PER_PAGE });
  const lastSample = useRef(null);

  const getUploadSessions = useCallback((params) => {
    lastQuery.current = params;
    return getUploadSessionsApi(params).then(response => {
      setSkeletonLoader(false);
      if (!response.ok) return;
      setSessions({ list: response.list, pagination: response.pagination });
      setLastUpdated(Date.now());
      // Speed is only meaningful while the same page of active sessions is being polled
      const activeBytes = sumBy(response.list.filter(session => session.status === SESSION_STATUS.ACTIVE), 'uploaded_bytes');
      const now = Date.now();
      const previous = lastSample.current;
      if (previous && previous.page === params.page && now > previous.at && activeBytes >= previous.bytes) {
        setSpeed((activeBytes - previous.bytes) / ((now - previous.at) / 1000));
      }
      lastSample.current = { at: now, bytes: activeBytes, page: params.page };
    });
  }, []);

  useEffect(() => {
    getUploadSessions({ page: 1, per_page: DEFAULT_PER_PAGE });
  }, [getUploadSessions]);

  const live = useLiveUploads();
  const { pagination } = sessions;
  const list = sessions.list.map(session => withLiveProgress(session, live[session.upload_id]));
  const activeSessions = list.filter(session => session.status === SESSION_STATUS.ACTIVE);
  const isActive = activeSessions.length > 0;
  const liveUploading = Object.values(live).filter(upload => upload.status === LIVE_STATUS.UPLOADING);
  const displaySpeed = liveUploading.length ? sumBy(liveUploading, 'speed') : speed;

  // A session started after the list loaded isn't in it yet; fetch once per new live upload
  const fetchedLiveIds = useRef(new Set());
  const liveIds = Object.keys(live).join(',');
  useEffect(() => {
    const missing = Object.keys(live).filter(id => !fetchedLiveIds.current.has(id) && !sessions.list.some(session => session.upload_id === id));
    if (!missing.length || skeletonLoader) return;
    missing.forEach(id => fetchedLiveIds.current.add(id));
    getUploadSessions(lastQuery.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIds, skeletonLoader]);

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = setInterval(() => getUploadSessions(lastQuery.current), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isActive, getUploadSessions]);

  const perPage = pagination ? pagination.per_page : DEFAULT_PER_PAGE;

  // Header totals cover active sessions when any exist, otherwise everything on this page
  const scope = isActive ? activeSessions : list;
  const totalFiles = sumBy(scope, 'total_files');
  const completedFiles = sumBy(scope, 'completed_files');
  const failedFiles = sumBy(scope, 'failed_files');
  const totalBytes = sumBy(scope, 'total_bytes');
  const uploadedBytes = sumBy(scope, 'uploaded_bytes');
  const percentage = totalBytes ? Math.min(100, (uploadedBytes / totalBytes) * 100) : 0;
  const done = totalFiles > 0 && completedFiles === totalFiles;
  const eta = displaySpeed > 0 && totalBytes > uploadedBytes ? formatDuration((totalBytes - uploadedBytes) / displaySpeed) : null;

  return (
    <div className="upload-progress">
      {list.length ?
        <Paper className={classes.header}>
          <div className={classes.headerTop}>
            <ProgressRing value={percentage} done={done} classes={classes} />
            <div className={classes.headline}>
              <div className={classes.title}>
                {isActive ?
                  <><CloudUploadIcon style={{ color: sessionStatusConfig.ACTIVE.color }} />{activeSessions.length} upload{activeSessions.length > 1 ? 's' : ''} in progress</> :
                  failedFiles ?
                    <><ErrorIcon style={{ color: sessionStatusConfig.FAILED.color }} />Uploads finished with errors</> :
                    <><CheckCircleIcon style={{ color: sessionStatusConfig.COMPLETED.color }} />No uploads in progress</>
                }
              </div>
              <div className={classes.subtitle}>
                <b>{completedFiles.toLocaleString()}</b> of <b>{totalFiles.toLocaleString()}</b> files completed
                {totalFiles > completedFiles ? <> · <b>{(totalFiles - completedFiles).toLocaleString()}</b> remaining</> : null}
                {failedFiles ? <> · <b style={{ color: sessionStatusConfig.FAILED.color }}>{failedFiles.toLocaleString()}</b> failed</> : null}
              </div>
            </div>
            <div className={classes.metrics}>
              <Metric classes={classes} icon={<StorageIcon />} label="Uploaded" value={`${getFileSize(uploadedBytes)} / ${getFileSize(totalBytes)}`} />
              {isActive ? <Metric classes={classes} icon={<SpeedIcon />} label="Speed" value={displaySpeed > 0 ? `${getFileSize(displaySpeed)}/s` : 'Calculating...'} /> : null}
              {isActive ? <Metric classes={classes} icon={<HourglassEmptyIcon />} label="Time left" value={eta || 'Calculating...'} /> : null}
            </div>
          </div>
        </Paper> : null
      }
      <Paper>
        <div className={classes.listHeader}>
          <div className={classes.listTitle}>
            Upload Sessions
            {pagination ? <span>{pagination.total.toLocaleString()}</span> : null}
          </div>
          <div className={classes.refreshInfo}>
            {lastUpdated ? `Updated ${moment(lastUpdated).format('hh:mm:ss A')}` : ''}
            <Tooltip title="Refresh" arrow>
              <IconButton size="small" onClick={() => getUploadSessions(lastQuery.current)}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </div>
        </div>
        {skeletonLoader ? <STableLoader count={7} size={6} /> :
          <>
            <div style={{ maxHeight: !list.length ? 'auto' : `${window.innerHeight - 330}px`, overflow: 'auto' }}>
              <Table className="mui-table-format" stickyHeader={true} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map(session =>
                    <TableRow hover tabIndex={-1} key={session.upload_id} className={classes.row}>
                      <TableCell>
                        <div className={classes.assetCell}>
                          <div className={classes.assetIcon}><FlightIcon /></div>
                          <div>
                            <div className={classes.assetName}>{session.asset_name || '--'}</div>
                            <Tooltip arrow title={`Upload ID: ${session.upload_id}`}>
                              <div className={classes.subText}>{capitalizeFirstLetter(session.asset_type)} · #{(session.upload_id || '').slice(0, 8)}</div>
                            </Tooltip>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={classes.filesCount}><b>{(session.completed_files || 0).toLocaleString()}</b> / {(session.total_files || 0).toLocaleString()}</div>
                        {session.failed_files ?
                          <div className={classes.subText} style={{ color: sessionStatusConfig.FAILED.color }}>{session.failed_files.toLocaleString()} failed</div> :
                          <div className={classes.subText}>completed</div>
                        }
                      </TableCell>
                      <TableCell><SessionProgress session={session} classes={classes} /></TableCell>
                      <TableCell><StatusChip status={session.status} classes={classes} /></TableCell>
                      <TableCell><RelativeTime value={session.created_at} classes={classes} /></TableCell>
                      <TableCell><RelativeTime value={session.completed_at || session.updated_at} classes={classes} /></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <Pagination
              pagination={pagination}
              onChangePage={(event, newPage) => getUploadSessions({ page: newPage + 1, per_page: perPage })}
              onChangeRowsPerPage={(event) => getUploadSessions({ page: 1, per_page: event.target.value })}
            />
            {!list.length ? <EmptyCollection title="No upload sessions yet" /> : null}
          </>
        }
      </Paper>
    </div>
  );
};

export default UploadProgress;
